import { CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface SelectionCardProps {
  label: string;
  isSelected: boolean;
  isDisabled?: boolean;
  /** Last remaining selected item — cannot be deselected */
  isLocked?: boolean;
  comingSoon?: boolean;
  onClick: () => void;
  /**
   * Compact variant used by the Starting-Universe multi-select pills.
   * Figma renders those with a 1px `border-foreground/40` unselected
   * border, a 12px check, and a `blur-[1.5px]` disabled state — distinct
   * from the wizard structure / rebalancing-methodology cards which use a
   * 2px `border-border` + 16px check. Default keeps the wizard look so
   * this change is scoped to the universe step only.
   */
  compact?: boolean;
  /** Extra classes on the label span (e.g. font-medium for Cyclicality). */
  labelClassName?: string;
  /**
   * Container padding override. Defaults to `p-2` (the universe pills).
   * The wizard's Portfolio Structure cards pass `px-3 py-2` to match Figma's
   * `px-[12px] py-[8px]` (StrategyHub.tsx:3259).
   */
  padding?: string;
}

// Multi-select pill — verbatim from figma UniverseSelection.tsx
// (pulled via Figma MCP). The exact class string for the Geographic
// Scope / Size Segment / Cyclicality pills in the live figma source:
//
//   p-2 rounded-lg border transition-colors
//   selected:   border-primary bg-primary/5 text-foreground
//   unselected: border-foreground/40 bg-card text-foreground
//                                            hover:border-primary/30
//   label:      text-[14px]
//   when selected, a CheckCircle2 w-3 h-3 text-primary appears right-justified.
//
// Earlier passes invented tighter sizing (rounded-md, text-[13px],
// py-1.5) inferring from the export — wrong. p-2 / rounded-lg /
// text-[14px] is what figma actually uses.
export const SelectionCard = ({
  label,
  isSelected,
  isDisabled = false,
  isLocked = false,
  comingSoon = false,
  onClick,
  compact = false,
  labelClassName,
  padding = 'p-2',
}: SelectionCardProps) => {
  const nonInteractive = isDisabled || isLocked;

  return (
    <div
      onClick={() => !nonInteractive && onClick()}
      className={cn(
        `${padding} rounded-lg border transition-colors`,
        isDisabled
          ? // Visible-but-disabled: a universe's fixed scopes stay legible,
            // just greyed out and non-interactive (no blur / heavy fade).
            'border-border/60 bg-muted/30 text-muted-foreground/70 cursor-not-allowed'
          : isLocked
            ? // The last remaining selection in a "at least one required"
              // group. It is still a SELECTION, so it keeps the selected
              // styling exactly — it used to carry `opacity-70`, which washed
              // out the one choice that mattered and made the user's active
              // pick read as disabled. The lock is signalled by the cursor
              // alone; the group's "At least one required" hint explains why.
              'border-primary bg-primary/5 text-foreground cursor-not-allowed'
            : isSelected
              ? 'border-primary bg-primary/5 text-foreground cursor-pointer'
              : compact
                ? 'border border-foreground/40 bg-card text-foreground cursor-pointer hover:border-primary/30'
                : 'border-2 border-border bg-card text-foreground cursor-pointer hover:border-primary/30',
      )}
    >
      <div className="flex items-center justify-between">
        <span className={cn('text-[14px]', labelClassName)}>{label}</span>
        {isSelected && !comingSoon && (
          <CheckCircle2
            className={cn(
              'text-primary flex-shrink-0',
              compact ? 'w-3 h-3' : 'w-4 h-4',
            )}
          />
        )}
        {comingSoon && (
          <Badge
            variant="secondary"
            className="bg-amber-500/10 text-amber-700 border-amber-500/30 font-normal text-[11px] px-1.5 h-5"
          >
            Coming Soon
          </Badge>
        )}
      </div>
    </div>
  );
};
